import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { forgotPassword, resetPassword } from '../api/real/authApi';
import { codeFromText } from '../utils/otp';

const AuthNav = () => (
  <nav className="bg-white border-b border-border px-4 sm:px-6 h-14 flex items-center justify-between sticky top-0 z-10">
    <Link to="/login" className="flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-primary transition-colors">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
      </svg>
      Back to Sign In
    </Link>
  </nav>
);

const Logo = () => (
  <Link to="/" className="inline-block">
    <div className="leading-none text-center">
      <div className="text-3xl font-black tracking-tight text-primary">SKINN</div>
      <div className="text-[0.5rem] font-bold tracking-[0.35em] text-accent -mt-0.5 uppercase">Thrive</div>
    </div>
  </Link>
);

const Spinner = () => (
  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
  </svg>
);

// ── Step 1: Enter email ───────────────────────────────────────────────────────
const EmailStep = ({ onOtpSent }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      onOtpSent(email.trim().toLowerCase());
    } catch {
      // Always proceed to OTP step (backend never reveals whether email exists)
      onOtpSent(email.trim().toLowerCase());
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="text-center mb-8">
        <Logo />
        <h1 className="text-2xl font-bold text-ink mt-5 mb-1">Reset your password</h1>
        <p className="text-ink-soft text-sm">Enter your email and we'll send you a reset code</p>
      </div>

      <div className="bg-white rounded-3xl p-8 border border-border" style={{boxShadow:'0 8px 40px rgba(124,31,166,0.10)'}}>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Email address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="input-field"
              required
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3.5 text-base flex items-center justify-center gap-2"
          >
            {loading ? <><Spinner /> Sending…</> : 'Send Reset Code →'}
          </button>
        </form>
        <p className="text-center text-sm text-ink-soft mt-5">
          Remembered it?{' '}
          <Link to="/login" className="text-primary font-semibold hover:underline">Sign in →</Link>
        </p>
      </div>
    </>
  );
};

// ── Step 2: Enter OTP + new password ─────────────────────────────────────────
const ResetStep = ({ email, onSuccess }) => {
  const [form, setForm] = useState({ otp: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (form.password !== form.confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email, form.otp.trim(), form.password);
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try { await forgotPassword(email); } catch {}
    setResending(false);
    setResent(true);
    setTimeout(() => setResent(false), 5000);
  };

  return (
    <>
      <div className="text-center mb-8">
        <Logo />
        <h1 className="text-2xl font-bold text-ink mt-5 mb-1">Enter reset code</h1>
        <p className="text-ink-soft text-sm">
          Check <strong>{email}</strong> for a 6-digit code
        </p>
      </div>

      <div className="bg-white rounded-3xl p-8 border border-border" style={{boxShadow:'0 8px 40px rgba(124,31,166,0.10)'}}>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>

          {error && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <svg className="w-4 h-4 text-red-500 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          {resent && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700">
              New code sent to {email}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Reset code</label>
            <input
              type="text"
              inputMode="numeric"
              value={form.otp}
              onChange={e => { setForm(f => ({...f, otp: codeFromText(e.target.value)})); setError(''); }}
              placeholder="000000"
              className="input-field text-center text-2xl tracking-[0.5em] font-mono"
              autoComplete="one-time-code"
              autoFocus
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">New password</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={form.password}
                onChange={e => { setForm(f => ({...f, password: e.target.value})); setError(''); }}
                placeholder="At least 6 characters"
                className="input-field pr-16"
                required
                minLength={6}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-ink-muted hover:text-primary transition-colors"
              >
                {showPwd ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Confirm new password</label>
            <input
              type={showPwd ? 'text' : 'password'}
              value={form.confirm}
              onChange={e => { setForm(f => ({...f, confirm: e.target.value})); setError(''); }}
              placeholder="Repeat your new password"
              className="input-field"
              required
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading || form.otp.length < 6}
            className="btn-primary w-full py-3.5 text-base flex items-center justify-center gap-2"
          >
            {loading ? <><Spinner /> Resetting…</> : 'Reset Password →'}
          </button>
        </form>

        <p className="text-center text-sm text-ink-soft mt-5">
          Didn't receive a code?{' '}
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-primary font-semibold hover:underline disabled:opacity-60"
          >
            {resending ? 'Sending…' : 'Resend code'}
          </button>
        </p>
      </div>
    </>
  );
};

// ── Step 3: Success ───────────────────────────────────────────────────────────
const SuccessStep = () => {
  const navigate = useNavigate();
  return (
    <>
      <div className="text-center mb-8">
        <Logo />
      </div>
      <div className="bg-white rounded-3xl p-8 border border-border text-center" style={{boxShadow:'0 8px 40px rgba(124,31,166,0.10)'}}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
          </svg>
        </div>
        <h2 className="text-xl font-bold text-ink mb-2">Password reset!</h2>
        <p className="text-ink-soft text-sm mb-6">Your password has been updated successfully. You can now sign in with your new password.</p>
        <button onClick={() => navigate('/login')} className="btn-primary w-full py-3.5">
          Sign In →
        </button>
      </div>
    </>
  );
};

// ── Main ForgotPasswordPage ───────────────────────────────────────────────────
export default function ForgotPasswordPage() {
  const [step, setStep] = useState('email'); // 'email' | 'reset' | 'success'
  const [email, setEmail] = useState('');

  const handleOtpSent = (e) => {
    setEmail(e);
    setStep('reset');
    toast.success('If this email is registered, you will receive a reset code shortly.');
  };

  const handleSuccess = () => {
    setStep('success');
    toast.success('Password reset successfully!');
  };

  return (
    <>
      <Helmet><title>Reset Password — Skinn Thrive</title></Helmet>
      <AuthNav />
      <div
        className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-12"
        style={{ background: 'linear-gradient(135deg, #F3E8FF 0%, #FCE4F3 100%)' }}
      >
        <div className="w-full max-w-md">
          {step === 'email' && <EmailStep onOtpSent={handleOtpSent} />}
          {step === 'reset' && <ResetStep email={email} onSuccess={handleSuccess} />}
          {step === 'success' && <SuccessStep />}
        </div>
      </div>
    </>
  );
}
